import React, { useState } from 'react'
import { useQuery } from 'react-query'
import { toast } from 'sonner'
import dayjs from 'dayjs'
import {
  Calendar,
  Clock,
  CheckCircle,
  User,
  Trophy,
  AlertCircle,
  TrendingUp,
} from 'lucide-react'

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Progress } from '@/components/ui/progress'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { workingDaysService } from '@/services/workingDaysService'

const WorkingDaysCard = () => {
  const [selectedYear, setSelectedYear] = useState(dayjs().year())
  const [selectedMonth, setSelectedMonth] = useState(dayjs().month() + 1)

  // Check if selected month is current month
  const isCurrentMonth = selectedYear === dayjs().year() && selectedMonth === dayjs().month() + 1

  // Fetch working days data
  const { data: workingDaysData, isLoading, error } = useQuery(
    ['workingDays', selectedYear, selectedMonth, isCurrentMonth],
    () => isCurrentMonth 
      ? workingDaysService.getCurrentMonthWorkingDays()
      : workingDaysService.getWorkingDaysData(selectedYear, selectedMonth),
    {
      onError: (err) => {
        // Don't show error message for validation errors (400 status)
        if (err.response?.status !== 400) {
          toast.error('Failed to fetch working days data: ' + (err.response?.data?.error || err.message))
        }
      }
    }
  )

  const data = workingDaysData?.data

  // Generate year options (from joining year to current year)
  const currentYear = dayjs().year()
  const joiningYear = data?.joiningDate ? dayjs(data.joiningDate).year() : currentYear - 1
  const yearOptions = []
  for (let year = joiningYear; year <= currentYear; year++) {
    yearOptions.push(year)
  }

  // Generate month options with validation
  const monthOptions = [
    { value: '1', label: 'January' },
    { value: '2', label: 'February' },
    { value: '3', label: 'March' },
    { value: '4', label: 'April' },
    { value: '5', label: 'May' },
    { value: '6', label: 'June' },
    { value: '7', label: 'July' },
    { value: '8', label: 'August' },
    { value: '9', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' }
  ]

  // Check if a month is valid for the selected year
  const isMonthValid = (year, month) => {
    if (!data?.joiningDate) return true
    const joiningDate = dayjs(data.joiningDate)
    const requestedDate = dayjs(`${year}-${month}-01`)
    return requestedDate.isSame(joiningDate, 'month') || requestedDate.isAfter(joiningDate, 'month')
  }

  const getAttendancePercentage = () => {
    if (!data || data.totalWorkingDaysInPeriod === 0) return 0
    return Math.round((data.daysWorkedInPeriod / data.totalWorkingDaysInPeriod) * 100)
  }

  const getAttendanceColor = (percentage) => {
    if (percentage >= 90) return 'text-emerald-600'
    if (percentage >= 75) return 'text-amber-600'
    if (percentage >= 60) return 'text-orange-600'
    return 'text-red-600'
  }

  const getProgressColor = (percentage) => {
    if (percentage >= 90) return 'bg-emerald-500'
    if (percentage >= 75) return 'bg-amber-500'
    if (percentage >= 60) return 'bg-orange-500'
    return 'bg-red-500'
  }

  if (error) {
    // Check if it's a validation error (400 status)
    if (error.response?.status === 400) {
      return (
        <Card>
          <CardContent className="py-6">
            <Alert className="border-amber-200 bg-amber-50">
              <AlertCircle className="h-4 w-4 text-amber-600" />
              <AlertDescription>
                <div className="space-y-2">
                  <p className="font-medium text-amber-800">Month Not Available</p>
                  <p className="text-sm text-amber-700">{error.response?.data?.error || 'This month is not available for viewing'}</p>
                  <p className="text-xs text-amber-600">
                    You can only view working days data for months after your joining date.
                  </p>
                </div>
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      )
    }
    
    return (
      <Card>
        <CardContent className="py-6 text-center">
          <p className="text-destructive">Failed to load working days data</p>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-primary/10">
            <Calendar className="h-5 w-5 text-primary" />
          </div>
          <div>
            <CardTitle className="text-base font-semibold">Working Days Tracker</CardTitle>
            <p className="text-sm text-muted-foreground">Monthly attendance overview</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Select
            value={String(selectedYear)}
            onValueChange={(value) => setSelectedYear(Number(value))}
          >
            <SelectTrigger className="w-24 h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {yearOptions.map(year => (
                <SelectItem key={year} value={String(year)}>{year}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={String(selectedMonth)}
            onValueChange={(value) => setSelectedMonth(Number(value))}
          >
            <SelectTrigger className="w-28 h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {monthOptions.map(month => {
                const isValid = isMonthValid(selectedYear, Number(month.value))
                return (
                  <SelectItem 
                    key={month.value} 
                    value={month.value}
                    disabled={!isValid}
                  >
                    {month.label}
                  </SelectItem>
                )
              })}
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-4">
            <Skeleton className="h-8 w-full" />
            <div className="grid grid-cols-3 gap-4">
              <Skeleton className="h-16" />
              <Skeleton className="h-16" />
              <Skeleton className="h-16" />
            </div>
            <Skeleton className="h-4 w-full" />
          </div>
        ) : data ? (
          <div className="space-y-6">
            {/* Month Header */}
            <div className="text-center">
              <h3 className="text-xl font-semibold text-foreground">{data.monthName}</h3>
              {data.effectiveStartDate && data.effectiveStartDate !== `${data.year}-${data.month.toString().padStart(2, '0')}-01` && (
                <p className="text-xs text-muted-foreground mt-1">
                  Working period: {dayjs(data.effectiveStartDate).format('MMM DD')} - {dayjs(data.effectiveEndDate).format('MMM DD, YYYY')}
                </p>
              )}
            </div>

            {/* Statistics */}
            <div className="grid grid-cols-3 gap-3">
              <div className="text-center p-3 bg-muted/50 rounded-lg border border-border">
                <div className="flex items-center justify-center gap-1 text-primary mb-1">
                  <Calendar className="h-4 w-4" />
                </div>
                <p className="text-2xl font-bold text-foreground">{data.totalWorkingDaysInMonth}</p>
                <p className="text-xs text-muted-foreground">Total Days</p>
              </div>
              <div className="text-center p-3 bg-emerald-50 rounded-lg border border-emerald-100">
                <div className="flex items-center justify-center gap-1 text-emerald-600 mb-1">
                  <User className="h-4 w-4" />
                </div>
                <p className="text-2xl font-bold text-emerald-600">{data.daysWorkedInPeriod}</p>
                <p className="text-xs text-emerald-600/70">
                  {data.isCurrentMonth ? 'Days Worked' : 'Days Worked'}
                </p>
              </div>
              <div className="text-center p-3 bg-orange-50 rounded-lg border border-orange-100">
                <div className="flex items-center justify-center gap-1 text-orange-600 mb-1">
                  <Clock className="h-4 w-4" />
                </div>
                <p className="text-2xl font-bold text-orange-600">{data.leaveDays}</p>
                <p className="text-xs text-orange-600/70">Leave Days</p>
              </div>
            </div>

            {/* Attendance Progress */}
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-foreground">Attendance Rate</span>
                <span className={`text-sm font-bold ${getAttendanceColor(getAttendancePercentage())}`}>
                  {getAttendancePercentage()}%
                </span>
              </div>
              <div className="h-2 bg-muted rounded-full overflow-hidden">
                <div 
                  className={`h-full transition-all duration-500 ${getProgressColor(getAttendancePercentage())}`}
                  style={{ width: `${getAttendancePercentage()}%` }}
                />
              </div>
            </div>

            {/* Holidays List */}
            {data.holidays && data.holidays.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold flex items-center gap-2 mb-3 text-foreground">
                  <Trophy className="h-4 w-4 text-amber-500" />
                  Holidays in {data.monthName}
                </h4>
                <div className="space-y-2">
                  {data.holidays.map((holiday, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 font-medium">
                        {dayjs(holiday.date).format('DD')}
                      </Badge>
                      <span className="text-sm text-foreground">{holiday.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Performance Indicator */}
            <div className={`text-center p-4 rounded-lg border ${
              getAttendancePercentage() >= 90 
                ? 'bg-emerald-50 border-emerald-200' 
                : getAttendancePercentage() >= 75 
                  ? 'bg-amber-50 border-amber-200'
                  : getAttendancePercentage() >= 60
                    ? 'bg-orange-50 border-orange-200'
                    : 'bg-red-50 border-red-200'
            }`}>
              <CheckCircle className={`h-5 w-5 mx-auto mb-2 ${
                getAttendancePercentage() >= 90 
                  ? 'text-emerald-600' 
                  : getAttendancePercentage() >= 75 
                    ? 'text-amber-600'
                    : getAttendancePercentage() >= 60
                      ? 'text-orange-600'
                      : 'text-red-600'
              }`} />
              <p className={`text-sm font-medium ${
                getAttendancePercentage() >= 90 
                  ? 'text-emerald-700' 
                  : getAttendancePercentage() >= 75 
                    ? 'text-amber-700'
                    : getAttendancePercentage() >= 60
                      ? 'text-orange-700'
                      : 'text-red-700'
              }`}>
                {getAttendancePercentage() >= 90 ? 'Excellent Attendance!' : 
                 getAttendancePercentage() >= 75 ? 'Good Attendance!' : 
                 getAttendancePercentage() >= 60 ? 'Average Attendance' : 'Needs Improvement'}
              </p>
            </div>
          </div>
        ) : (
          <div className="text-center py-8">
            <p className="text-muted-foreground">No data available</p>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

export default WorkingDaysCard
